import { useEffect, useRef, useState } from "react";

const slides = [
  {
    image: "/images/v1/img12.jpg",
    eyebrow: "Wildlife Information & Tracking System",
    headline: ["Where Data Meets", "Conservation"],
    sub: "WITS empowers rangers, prosecutors, and communities across Africa with real-time intelligence to protect wildlife and secure the convictions that matter.",
    cta:           { label: "Explore the Platform", href: "/features" },
    ctaSecondary:  { label: "Report an Incident",   href: "/report-incident" },
  },
  {
    image: "/images/v1/img12.jpg",
    eyebrow: "Evidence-Based Action",
    headline: ["Turning Data", "Into Justice"],
    sub: "From the first seizure report to a courtroom conviction — WITS connects every link in the chain, making impunity harder and accountability inevitable.",
    cta:           { label: "Case Precedents", href: "/case-precedents" },
    ctaSecondary:  { label: "Get Involved",    href: "/get-involved" },
  },
  {
    image: "/images/v1/img12.jpg",
    eyebrow: "Across the Continent",
    headline: ["Protecting Africa's", "Natural Heritage"],
    sub: "Operating across 5+ countries, WITS aggregates incident data, tracks trafficking networks, and powers policy reform at every level of governance.",
    cta:           { label: "Our Story",       href: "/about-us" },
    ctaSecondary:  { label: "Partner with Us", href: "/get-involved" },
  },
];

export default function HeroCarousel() {
  const [current, setCurrent]   = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = (next: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(next);
      setAnimating(false);
    }, 500);
  };

  useEffect(() => {
    timerRef.current = setTimeout(() => goTo((current + 1) % slides.length), 7000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const slide = slides[current];

  return (
    <section className="relative w-full h-screen min-h-[600px] max-h-[900px] overflow-hidden bg-[#052e16]">

      {/* ── Background images with Ken Burns ── */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
          aria-hidden={i !== current}
        >
          <img
            src={s.image}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover select-none ${
              i === current ? 'animate-ken-burns' : ''
            }`}
          />
        </div>
      ))}

      {/* ── Cinematic gradient overlays ── */}
      {/* Base scrim — takes the whole image darker so no area is raw-bright */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      {/* Left-to-right: deeply dark on text side, fades toward right edge */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#052e16]/95 via-[#052e16]/70 to-[#052e16]/10 pointer-events-none" />
      {/* Top-to-bottom: darkens bright skies, lifts from bottom */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-[#052e16]/80 pointer-events-none" />

      {/* ── Slide content ── */}
      <div className="relative z-10 h-full flex flex-col justify-center px-6 lg:px-20 xl:px-28">
        <div className="max-w-2xl">

          {/* Eyebrow */}
          <div
            key={`eyebrow-${current}`}
            className="flex items-center gap-3 mb-7 animate-fade-up"
            style={{ animationDelay: '0.05s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <span className="block h-px w-10 bg-green-400" />
            <span
              className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {slide.eyebrow}
            </span>
          </div>

          {/* Headline */}
          <h1
            key={`headline-${current}`}
            className="text-5xl lg:text-7xl font-bold text-white leading-[1.07] mb-7 animate-fade-up"
            style={{
              fontFamily: 'var(--font-display)',
              textShadow: '0 2px 24px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.4)',
              animationDelay: '0.2s',
              opacity: 0,
              animationFillMode: 'forwards',
            }}
          >
            <span className="block">{slide.headline[0]}</span>
            <span className="block text-green-400">{slide.headline[1]}</span>
          </h1>

          {/* Subtitle */}
          <p
            key={`sub-${current}`}
            className="text-white/85 text-base lg:text-lg font-light leading-relaxed mb-10 max-w-lg animate-fade-up"
            style={{
              fontFamily: 'var(--font-body)',
              textShadow: '0 1px 10px rgba(0,0,0,0.6)',
              animationDelay: '0.35s',
              opacity: 0,
              animationFillMode: 'forwards',
            }}
          >
            {slide.sub}
          </p>

          {/* CTAs */}
          <div
            key={`cta-${current}`}
            className="flex flex-wrap gap-4 animate-fade-up"
            style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <a
              href={slide.cta.href}
              className="px-7 py-3.5 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-all duration-300 hover:shadow-2xl hover:shadow-green-900/50 hover:-translate-y-0.5 active:translate-y-0"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {slide.cta.label} →
            </a>
            <a
              href={slide.ctaSecondary.href}
              className="px-7 py-3.5 rounded-full border border-white/35 text-white text-sm font-medium hover:border-white/70 hover:bg-white/10 transition-all duration-300"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {slide.ctaSecondary.label}
            </a>
          </div>
        </div>
      </div>

      {/* ── Slide indicators ── */}
      <div className="absolute bottom-10 left-6 lg:left-20 xl:left-28 z-10 flex items-center gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`transition-all duration-500 rounded-full ${
              i === current
                ? 'w-8 h-2 bg-green-400'
                : 'w-2 h-2 bg-white/35 hover:bg-white/60'
            }`}
          />
        ))}
        <span
          className="ml-2 text-white/30 text-xs tabular-nums"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* ── Scroll hint ── */}
      <div className="absolute bottom-10 right-6 lg:right-20 z-10 flex flex-col items-center gap-2">
        <span
          className="text-white/30 text-[10px] tracking-[0.2em] uppercase rotate-90 mb-2"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          Scroll
        </span>
        <div className="w-px h-10 bg-white/20 overflow-hidden rounded-full">
          <div className="w-full h-1/2 bg-green-400 rounded-full animate-scroll-hint" />
        </div>
      </div>
    </section>
  );
}
